const DEFAULT_MAX_LINE_LENGTH = 34;

const getDisplayText = (value: unknown): string => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).replace(/\s+/g, ' ').trim();
};

const getFirstDisplayValue = (...values: unknown[]): unknown => (
  values.find((value) => getDisplayText(value) !== '')
);

const MULTI_WORD_MANUFACTURERS = [
  'Alfa Romeo',
  'Aston Martin',
  'DS Automobiles',
  'Great Wall',
  'Land Rover',
  'Mercedes Benz',
  'Rolls Royce',
];

const getPropertyValue = (node: any, aliases: string[]): unknown => {
  const properties = node?.properties || {};
  for (const alias of aliases) {
    const value = getFirstDisplayValue(properties[alias], node?.[alias]);
    if (value !== undefined) return value;
  }
  return undefined;
};

const getDescriptionParts = (description: unknown) => {
  const text = getDisplayText(description);
  const segments = text.split(',').map(part => part.trim()).filter(Boolean);
  const identity = segments[0] || '';
  const identityParts = identity.split(' ').filter(Boolean);
  const yearMatch = text.match(/\b(?:19|20)\d{2}\b/);
  const multiWordManufacturer = MULTI_WORD_MANUFACTURERS.find(candidate => (
    identity.toLocaleLowerCase().startsWith(`${candidate.toLocaleLowerCase()} `)
  ));
  const manufacturer = multiWordManufacturer || identityParts[0] || undefined;
  const manufacturerWordCount = multiWordManufacturer?.split(' ').length || (manufacturer ? 1 : 0);

  return {
    manufacturer,
    model: identityParts.slice(manufacturerWordCount).join(' ') || undefined,
    modelYear: yearMatch?.[0],
  };
};

const normalizeModel = (value: unknown, manufacturer: unknown): string | undefined => {
  const text = getDisplayText(value).split(',')[0].trim();
  if (!text) return undefined;

  const manufacturerText = getDisplayText(manufacturer);
  if (!manufacturerText) return text;

  const manufacturerPrefix = `${manufacturerText} `;
  return text.toLocaleLowerCase().startsWith(manufacturerPrefix.toLocaleLowerCase())
    ? text.slice(manufacturerPrefix.length).trim() || undefined
    : text;
};

export const getCarNodeLabelValues = (node: any) => {
  const description = getFirstDisplayValue(
    node?.properties?.description,
    node?.properties?.car_description,
    node?.car_description,
    node?.description,
  );
  const parsedDescription = getDescriptionParts(description);
  const manufacturer = getFirstDisplayValue(
    getPropertyValue(node, [
      'manufacturer',
      'manufacturer_name',
      'make',
      'make_name',
      'car_make',
      'vehicle_make',
      'brand',
      'brand_name',
      'marque',
      'car_manufacturer',
      'vehicle_manufacturer',
    ]),
    parsedDescription.manufacturer,
  );
  const explicitModel = getPropertyValue(node, [
    'model',
    'model_name',
    'car_model',
    'vehicle_model',
    'vehicle_model_name',
  ]);

  return {
    registrationNumber: getFirstDisplayValue(
      node?.name,
      getPropertyValue(node, [
        'car_reg_number',
        'registration_number',
        'registrationNumber',
        'reg_number',
      ]),
    ),
    manufacturer,
    model: normalizeModel(explicitModel, manufacturer) || parsedDescription.model,
    modelYear: getFirstDisplayValue(
      getPropertyValue(node, [
        'model_year',
        'modelYear',
        'vehicle_year',
        'vehicleYear',
        'year_model',
        'manufacture_year',
        'manufacturing_year',
        'car_year',
        'year',
      ]),
      parsedDescription.modelYear,
    ),
    leasingCompany: getFirstDisplayValue(
      node?.properties?.leasing_company,
      node?.leasing_company,
    ),
  };
};

export const wrapCarLabelText = (
  value: unknown,
  maxLineLength: number = DEFAULT_MAX_LINE_LENGTH,
): string[] => {
  const text = getDisplayText(value);
  if (!text) return [];

  const lines: string[] = [];
  let currentLine = '';

  text.split(' ').forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (currentLine && candidate.length > maxLineLength) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  });

  if (currentLine) lines.push(currentLine);
  return lines;
};

export const buildCarNodeLabel = ({
  registrationNumber,
  manufacturer,
  model,
  modelYear,
  leasingCompany,
  leasedFromLabel,
}: {
  registrationNumber: unknown;
  manufacturer?: unknown;
  model?: unknown;
  modelYear?: unknown;
  leasingCompany?: unknown;
  leasedFromLabel: string;
}): string => {
  const lines = wrapCarLabelText(registrationNumber);
  const vehicleName = [manufacturer, model].map(getDisplayText).filter(Boolean).join(' ');
  lines.push(...wrapCarLabelText(vehicleName));
  lines.push(...wrapCarLabelText(modelYear));

  const leasingCompanyText = getDisplayText(leasingCompany);
  if (leasingCompanyText) {
    lines.push(...wrapCarLabelText(`${leasedFromLabel}: ${leasingCompanyText}`));
  }

  return lines.join('\n');
};
